/**
 * Sage warehouse master list (hardcoded).
 *
 * Why hardcoded?
 *   The Sage IM_Warehouse master table is not synced into our database, so
 *   English descriptions do not live anywhere queryable. The 3/10/2026 meeting
 *   between Scott, Hadi, Emily, and Robin accepted the tech debt of keeping
 *   this list in the frontend until someone asks for a live sync.
 *
 *   When Sage adds a new warehouse code, users can still type it in manually
 *   via the free-text fallback in `SearchableDropdown` — no order is blocked.
 *
 * Maintenance:
 *   To add/remove a warehouse, edit `SAGE_WAREHOUSES` below. Flip `highlighted`
 *   to control whether it appears in the default "Active" group or the expanded
 *   "Other" group.
 *
 * Source of truth:
 *   warehouse_code_list.xlsx provided by Scott on 2026-04-09 (attached to
 *   document review a6c6ad2c-aeb0-47be-989b-2a6231318ba8). "Highlighted" set
 *   to Scott's "Highlighted Only" sheet; "inactive" reflects the Status column.
 *
 * Reconciliation vs production Sage DB (2026-04-09):
 *   - `item_warehouses` (synced from Sage IM_ItemWarehouse) has 80 distinct
 *     codes. All 80 are present in this file.
 *   - This file has ONE extra code relative to item_warehouses: 416 King
 *     Concrete Supplies. That's expected — item_warehouses only contains
 *     warehouses with at least one stocked item, and 416 has 0 stocked items
 *     in Sage today. It still lives in Sage's IM_Warehouse master and Scott
 *     marked it highlighted because the order team uses it.
 *   - Real order usage past 12 months (sage_sales_orders.warehouse_code):
 *       000 ChemMasters         349 orders
 *       003 Solon, OHIO          36 orders
 *       410 Gateway-H             4 orders
 *       412 Gateway-S             2 orders
 *       155 Guilford Warehouse    1 order
 *     All 5 actively used warehouses are on Scott's highlighted list.
 *     The other 4 highlighted rows (150, 156, 411, 416) had 0 orders but
 *     Scott kept them based on forward-looking knowledge.
 */

export interface SageWarehouse {
  /** 3-char Sage warehouse code (stored in OrderRecord.warehouse_code). */
  code: string;
  /** English description shown to users in the dropdown. */
  label: string;
  /**
   * Whether this warehouse is on Scott's "Highlighted Only" list (the
   * short list of warehouses the order team actually picks day-to-day).
   * Highlighted entries appear in the primary group; others in the "Show
   * all" group. A free-text value that matches neither is still accepted
   * on blur for forward-compatibility with new Sage codes.
   */
  highlighted: boolean;
  /**
   * Whether Sage has marked this warehouse inactive. We still list it so
   * historical orders render readable labels, but the UI visually
   * de-emphasizes it (shown as meta text "Inactive").
   */
  inactive: boolean;
}

/**
 * The canonical list. Ordered numerically by code (which roughly clusters
 * related groups: 0–19 = Chemmasters + tolls, 100+ = partner warehouses,
 * 400+ = Gateway/Harrison/Seymours region).
 */
export const SAGE_WAREHOUSES: readonly SageWarehouse[] = [
  { code: '000', label: 'ChemMasters', highlighted: true, inactive: false },
  { code: '001', label: 'REWORK', highlighted: false, inactive: false },
  { code: '002', label: 'REPACKAGE', highlighted: false, inactive: false },
  { code: '003', label: 'Solon, OHIO', highlighted: true, inactive: false },
  { code: '004', label: 'Toll Solutions', highlighted: false, inactive: false },
  { code: '005', label: 'Sportsy Bag', highlighted: false, inactive: true },
  { code: '006', label: 'Statewide Warehouse', highlighted: false, inactive: false },
  { code: '007', label: 'Chemical Solvents Inc.', highlighted: false, inactive: true },
  { code: '010', label: 'Madison - Vexcon', highlighted: false, inactive: true },
  { code: '011', label: 'Advanced Polymer', highlighted: false, inactive: true },
  { code: '012', label: 'Axcel', highlighted: false, inactive: false },
  { code: '013', label: 'FBC Chemical', highlighted: false, inactive: false },
  { code: '014', label: 'Dependable', highlighted: false, inactive: false },
  { code: '015', label: 'Cleveland Bottle', highlighted: false, inactive: true },
  { code: '016', label: 'Snyder', highlighted: false, inactive: false },
  { code: '017', label: 'PChem-ChemMasters', highlighted: false, inactive: false },
  { code: '018', label: 'SurfKoat', highlighted: false, inactive: false },
  { code: '019', label: 'JTM Products', highlighted: false, inactive: false },
  { code: '100', label: 'EMCO', highlighted: false, inactive: false },
  { code: '101', label: 'Jamestown Macadom', highlighted: false, inactive: true },
  { code: '102', label: 'FBC Chemical - Mars, PA', highlighted: false, inactive: false },
  { code: '103', label: 'Speedway Redi Mix, Inc.', highlighted: false, inactive: true },
  { code: '104', label: 'Kumpfor Zone Warehousing, Inc', highlighted: false, inactive: true },
  { code: '105', label: 'Kumpfor Zone Warehousing', highlighted: false, inactive: true },
  { code: '106', label: 'Waco - Akron', highlighted: false, inactive: true },
  { code: '107', label: 'Waco Equipment - Cleveland', highlighted: false, inactive: true },
  { code: '108', label: 'Waco Equipment - Columbus', highlighted: false, inactive: true },
  { code: '109', label: 'Waco Formworks - Akron', highlighted: false, inactive: true },
  { code: '110', label: 'Waco Equipment-Cleveland', highlighted: false, inactive: true },
  { code: '111', label: 'Waco Equipment-Columbus', highlighted: false, inactive: true },
  { code: '112', label: 'Waco Formworks - N. Canton', highlighted: false, inactive: true },
  { code: '113', label: 'Waco Formworks - Youngstown', highlighted: false, inactive: true },
  { code: '114', label: 'Waco Equipment Pleasantville', highlighted: false, inactive: true },
  { code: '115', label: 'Waco Formworks - Monroeville', highlighted: false, inactive: true },
  { code: '116', label: 'Waco Formworks - S. Charleston', highlighted: false, inactive: true },
  { code: '118', label: 'Inactive', highlighted: false, inactive: true },
  { code: '119', label: 'Alabama Construction Supply', highlighted: false, inactive: true },
  { code: '120', label: 'Alabama Construction Supply', highlighted: false, inactive: true },
  { code: '121', label: 'United Rentals - Kitchener', highlighted: false, inactive: true },
  { code: '122', label: 'United Rentals - Kitchener', highlighted: false, inactive: true },
  { code: '123', label: 'Hotham Building Materials', highlighted: false, inactive: true },
  { code: '124', label: 'State Highway Supply', highlighted: false, inactive: true },
  { code: '125', label: 'State Highway Supply', highlighted: false, inactive: true },
  { code: '126', label: 'A2Z Rentals, Inc.', highlighted: false, inactive: true },
  { code: '127', label: 'Burlington Contractor', highlighted: false, inactive: true },
  { code: '128', label: 'Gatti-Morrison', highlighted: false, inactive: true },
  { code: '129', label: 'Buffalo Concrete Accessories', highlighted: false, inactive: true },
  { code: '130', label: 'Dealer Depot', highlighted: false, inactive: true },
  { code: '131', label: 'CMA Supply - Ft. Wayne', highlighted: false, inactive: true },
  { code: '132', label: 'CMA Supply - Ft. Wayne', highlighted: false, inactive: true },
  { code: '133', label: 'CMA Supply - South Bend', highlighted: false, inactive: true },
  { code: '134', label: 'CMA Supply - South Bend', highlighted: false, inactive: true },
  { code: '135', label: 'Allied Dealers Supply', highlighted: false, inactive: true },
  { code: '136', label: 'Action Concrete Supplies', highlighted: false, inactive: true },
  { code: '137', label: 'CMA Supply - Indianapolis', highlighted: false, inactive: true },
  { code: '138', label: 'CMA Supply - Indianapolis', highlighted: false, inactive: true },
  { code: '139', label: 'DiStefano', highlighted: false, inactive: true },
  { code: '140', label: 'Bon Tool', highlighted: false, inactive: true },
  { code: '141', label: 'MPM Rental Inc.', highlighted: false, inactive: true },
  { code: '142', label: 'Dominion Equipment', highlighted: false, inactive: true },
  { code: '143', label: 'United Rentals - Lethbridge', highlighted: false, inactive: true },
  { code: '144', label: 'Cencotech, Inc.', highlighted: false, inactive: true },
  { code: '145', label: 'Jett & Company', highlighted: false, inactive: true },
  { code: '146', label: 'Southern Fastener', highlighted: false, inactive: true },
  { code: '147', label: 'CSS Warehouse', highlighted: false, inactive: true },
  { code: '148', label: 'Five Star Equipment & Supply', highlighted: false, inactive: true },
  { code: '149', label: 'A&N Warehouse', highlighted: false, inactive: false },
  { code: '150', label: 'Vieira Warehouse', highlighted: true, inactive: false },
  { code: '151', label: 'National Concrete Accessories', highlighted: false, inactive: true },
  { code: '152', label: 'Site Source', highlighted: false, inactive: true },
  { code: '153', label: 'Jenks Warehouse', highlighted: false, inactive: true },
  { code: '154', label: 'A&H Concrete', highlighted: false, inactive: false },
  { code: '155', label: 'Guilford Warehouse', highlighted: true, inactive: false },
  // Scott chose to keep Mathis Kelley on the highlighted list even though
  // Sage marks it Inactive — leave as-is per the source spreadsheet.
  { code: '156', label: 'Mathis Kelley Construction', highlighted: true, inactive: true },
  { code: '410', label: 'Gateway-H', highlighted: true, inactive: false },
  // Scott's list shows Gateway (411) as both highlighted and inactive.
  { code: '411', label: 'Gateway', highlighted: true, inactive: true },
  { code: '412', label: 'Gateway-S', highlighted: true, inactive: false },
  { code: '413', label: 'Gateway-A', highlighted: false, inactive: true },
  { code: '414', label: 'Track Logistics Warehouse', highlighted: false, inactive: false },
  { code: '415', label: 'Moving Solutions', highlighted: false, inactive: false },
  { code: '416', label: 'King Concrete Supplies', highlighted: true, inactive: false },
];

/** Convenience: just the highlighted entries. */
export const HIGHLIGHTED_WAREHOUSES: readonly SageWarehouse[] =
  SAGE_WAREHOUSES.filter((w) => w.highlighted);

/**
 * Look up a warehouse by code. Matches are case-insensitive. Returns `null`
 * when the code is not in the hardcoded list (the UI should still accept it
 * as a free-text value for forward-compatibility with new Sage codes).
 */
export function findWarehouseByCode(code: string): SageWarehouse | null {
  const normalized = code.trim().toUpperCase();
  return SAGE_WAREHOUSES.find((w) => w.code.toUpperCase() === normalized) ?? null;
}
